import { Toaster } from "./components/ui/toaster";
import { Toaster as Sonner } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "./contexts/AuthContext";
import { LanguageProvider } from "./contexts/LanguageContext";
import { CurrencyProvider } from "./contexts/CurrencyContext";

// Auth
import ProtectedRoute from "./features/auth/components/ProtectedRoute";
import AdminRoute from "./features/auth/components/AdminRoute";
import Auth from "./features/auth/pages/Auth";
import Login from "./features/auth/pages/Login";
import Register from "./features/auth/pages/Register";
import AdminRegister from "./features/auth/pages/AdminRegister";
import InvestorRegister from "./features/auth/pages/InvestorRegister";
import ProjectHolderRegister from "./features/auth/pages/ProjectHolderRegister";

// Investor
import InvestorDashboard from "./features/investor/pages/InvestorDashboard";
import InvestorSettings from "./features/investor/pages/InvestorSettings";
import InvestorNotifications from "./features/investor/pages/InvestorNotifications";
import WaitingApprovalInvestor from "./features/investor/pages/WaitingApprovalInvestor";

// Project Holder
import ProjectHolderDashboard from "./features/project-holder/pages/ProjectHolderDashboard";
import ProjectHolderSettings from "./features/project-holder/pages/ProjectHolderSettings";
import ProjectHolderNotifications from "./features/project-holder/pages/ProjectHolderNotifications";
import WaitingApprovalProjectHolder from "./features/project-holder/pages/WaitingApprovalProjectHolder";

// Projects
import Projects from "./features/projects/pages/Projects";
import ProjectDetail from "./features/projects/pages/ProjectDetail";
import AddProject from "./features/projects/pages/AddProject";
import EditProject from "./features/projects/pages/EditProject";

// Project Sales
import ProjectSales from "./features/project-sales/pages/ProjectSales";
import ProjectSaleDetail from "./features/project-sales/pages/ProjectSaleDetail";
import UnifiedProjectDetail from "./features/project-sales/pages/UnifiedProjectDetail";
import CreateProjectSale from "./features/project-sales/pages/CreateProjectSale";

// Project Investments
import UnifiedInvestmentDetail from "./features/project-investments/pages/UnifiedInvestmentDetail";

// Admin
import AdminDashboard from "./features/admin/pages/AdminDashboard";
import AdminSettings from "./features/admin/pages/AdminSettings";
import AdminCreateAccount from "./features/admin/pages/AdminCreateAccount";
import UserDetail from "./features/admin/pages/UserDetail";
import DataMigration from "./features/admin/pages/DataMigration";

// Public pages
import PublicHome from "./pages/PublicHome";
import About from "./pages/About";
import HowItWorks from "./pages/HowItWorks";
import Experts from "./pages/Experts";
import Program from "./pages/Program";
import Partners from "./pages/Partners";
import Achievements from "./pages/Achievements";
import Investor from "./pages/Investor";
import FinancialAlternatives from "./pages/FinancialAlternatives";
import WaitingApproval from "./pages/WaitingApproval";
import SettingsRedirect from "./pages/SettingsRedirect";
import NotFound from "./pages/NotFound";
import SchemaTest from "./pages/SchemaTest";

const queryClient = new QueryClient();

const App = () => (
    <QueryClientProvider client={queryClient}>
        <AuthProvider>
            <LanguageProvider>
                <CurrencyProvider>
                    <TooltipProvider>
                        <Toaster />
                        <Sonner />
                        <BrowserRouter>
                            <Routes>
                                <Route path="/" element={<PublicHome />} />
                                <Route path="/home" element={<PublicHome />} />
                                <Route path="/about" element={<About />} />
                                <Route path="/how-it-works" element={<HowItWorks />} />
                                <Route path="/experts" element={<Experts />} />
                                <Route path="/program" element={<Program />} />

                                <Route path="/projects" element={<Projects />} />
                                <Route path="/project-sales" element={<ProjectSales />} />
                                <Route path="/investor" element={<Investor />} />
                                <Route path="/partners" element={<Partners />} />
                                <Route path="/achievements" element={<Achievements />} />
                                <Route path="/financial-alternatives" element={<FinancialAlternatives />} />
                                <Route path="/auth" element={<Auth />} />
                                <Route path="/login" element={<Login />} />
                                <Route path="/register" element={<Register />} />
                                <Route path="/investor-register" element={<InvestorRegister />} />
                                <Route path="/project-holder-register" element={<ProjectHolderRegister />} />
                                <Route path="/waiting-approval" element={<WaitingApproval />} />
                                <Route path="/waiting-approval/investor" element={<WaitingApprovalInvestor />} />
                                <Route path="/waiting-approval/project-holder" element={<WaitingApprovalProjectHolder />} />
                                <Route
                                    path="/dashboard/investor"
                                    element={
                                        <ProtectedRoute requiredUserType="investor" allowPending={true}>
                                            <InvestorDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/dashboard/project-holder"
                                    element={
                                        <ProtectedRoute requiredUserType="project-holder" allowPending={true}>
                                            <ProjectHolderDashboard />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/projects/new"
                                    element={
                                        <ProtectedRoute requiredUserType="project-holder">
                                            <AddProject />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/projects/:projectId" element={<ProjectDetail />} />
                                <Route
                                    path="/projects/:projectId/edit"
                                    element={
                                        <ProtectedRoute requiredUserType="project-holder">
                                            <EditProject />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route path="/project-sales/:projectSaleId" element={<ProjectSaleDetail />} />
                                <Route path="/project-sales/preview" element={<UnifiedProjectDetail />} />
                                <Route path="/project-investments/preview" element={<UnifiedInvestmentDetail />} />
                                <Route path="/settings" element={<SettingsRedirect />} />
                                <Route
                                    path="/settings/project-holder"
                                    element={
                                        <ProtectedRoute requiredUserType="project-holder">
                                            <ProjectHolderSettings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/settings/investor"
                                    element={
                                        <ProtectedRoute requiredUserType="investor">
                                            <InvestorSettings />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/notifications/investor"
                                    element={
                                        <ProtectedRoute requiredUserType="investor">
                                            <InvestorNotifications />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/notifications/project-holder"
                                    element={
                                        <ProtectedRoute requiredUserType="project-holder">
                                            <ProjectHolderNotifications />
                                        </ProtectedRoute>
                                    }
                                />
                                <Route
                                    path="/admin/settings"
                                    element={
                                        <AdminRoute>
                                            <AdminSettings />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin"
                                    element={
                                        <AdminRoute>
                                            <AdminDashboard />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/users/:userId"
                                    element={
                                        <AdminRoute>
                                            <UserDetail />
                                        </AdminRoute>
                                    }
                                />
                                <Route path="/admin/register" element={<AdminRegister />} />
                                <Route
                                    path="/admin/create-account"
                                    element={
                                        <AdminRoute>
                                            <AdminCreateAccount />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/create-project-sale"
                                    element={
                                        <AdminRoute>
                                            <CreateProjectSale />
                                        </AdminRoute>
                                    }
                                />
                                <Route
                                    path="/admin/data-migration"
                                    element={
                                        <AdminRoute>
                                            <DataMigration />
                                        </AdminRoute>
                                    }
                                />
                                <Route path="/schema-test" element={<SchemaTest />} />
                                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTES */}
                                <Route path="*" element={<NotFound />} />
                            </Routes>
                        </BrowserRouter>
                    </TooltipProvider>
                </CurrencyProvider>
            </LanguageProvider>
        </AuthProvider>
    </QueryClientProvider>
);

export default App;
